// Native blocking: manage blocked users list in Capacitor Preferences
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const STORAGE_KEY_PREFIX = 'graphdebate:blocked-users';

export type BlockedUser = {
  userId: string;
  pseudo?: string;
  picture?: string;
  blockedAt: number;
};

const makeStorageKey = (websiteKey: string) => `${STORAGE_KEY_PREFIX}:${websiteKey}`;

const isNative = () => Capacitor.isNativePlatform();

export async function getBlockedUsers(websiteKey: string): Promise<BlockedUser[]> {
  if (!isNative() || !websiteKey) return [];
  try {
    const { value } = await Preferences.get({ key: makeStorageKey(websiteKey) });
    if (!value) return [];
    const parsed = JSON.parse(value) as BlockedUser[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(elt => elt?.userId);
  } catch (error) {
    console.error('[blockedUsers] Failed to read blocked users', error);
    return [];
  }
}

export async function saveBlockedUsers(websiteKey: string, users: BlockedUser[]) {
  if (!isNative() || !websiteKey) return;
  try {
    await Preferences.set({
      key: makeStorageKey(websiteKey),
      value: JSON.stringify(users),
    });
  } catch (error) {
    console.error('[blockedUsers] Failed to save blocked users', error);
  }
}

export async function getBlockedUserIds(websiteKey: string): Promise<Set<string>> {
  const users = await getBlockedUsers(websiteKey);
  return new Set(users.map(u => u.userId));
}

export async function blockUser(
  websiteKey: string,
  user: { userId: string; pseudo?: string; picture?: string },
) {
  if (!isNative() || !websiteKey || !user?.userId) return;
  try {
    const current = await getBlockedUsers(websiteKey);
    if (current.some(u => u.userId === user.userId)) return;
    const updated: BlockedUser[] = [
      ...current,
      { userId: user.userId, pseudo: user.pseudo, picture: user.picture, blockedAt: Date.now() },
    ];
    await saveBlockedUsers(websiteKey, updated);
  } catch (error) {
    console.error('[blockedUsers] Failed to block user', error);
  }
}

export async function unblockUser(websiteKey: string, userId: string) {
  if (!isNative() || !websiteKey || !userId) return;
  try {
    const current = await getBlockedUsers(websiteKey);
    const updated = current.filter(u => u.userId !== userId);
    await saveBlockedUsers(websiteKey, updated);
  } catch (error) {
    console.error('[blockedUsers] Failed to unblock user', error);
  }
}

export async function isUserBlocked(websiteKey: string, userId?: string): Promise<boolean> {
  if (!userId) return false;
  const ids = await getBlockedUserIds(websiteKey);
  return ids.has(userId);
}
